import { FormFieldType } from "@app/components/forms/types/schema";
import { CommonErrorModal } from "@app/components/modals/CommonErrorModal";
import {
  PostingUpdateModal,
  PostingGroupChangeModal,
} from "@app/components/modals/PostingSelectorModal";
import { PreviewModal } from "@app/components/modals/PreviewModal";
import { SharePostingModal } from "@app/components/modals/ShareModal";
import {
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  MoreMenu,
  useModal,
} from "@shortsworks/ui";
import { cn } from "@shortsworks/ui/lib/utils";
import {
  useDeleteStory,
  useGetStoryGroupList,
  useUpdateStoryMetadata,
  useGetStories,
} from "@shortsworks/user-api";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeftRight,
  Group,
  Pencil,
  Pin,
  Share2,
  Trash2,
  PinIcon,
  Eye,
} from "lucide-react";

export interface PostingCardProps {
  storyId: string;
  selectedIds: string[];
  thumbnail: string;
  title?: string;
  description?: string;
  groupIds?: string[];
  selected?: boolean;
  onSelect?: (id: string, selected: boolean) => void;
  hideMoreMenu?: boolean;
  isPinned?: boolean;
  params: {
    draft?: boolean;
    hidden?: boolean;
  };
}

export const moreMenuItems = [
  {
    title: "미리보기",
    type: "preview",
    icon: Eye,
  },
  {
    title: "제목 수정",
    type: "edit_title",
    icon: Pencil,
  },
  {
    title: "공지로 등록",
    type: "set_as_notice",
    icon: Pin,
  },
  {
    title: "그룹 변경",
    type: "change_group",
    icon: Group,
  },
  {
    title: "퍼가기",
    type: "share",
    icon: Share2,
  },
  {
    title: "임시 저장으로 이동",
    type: "move_to_draft",
    icon: ArrowLeftRight,
  },
  {
    title: "포스팅으로 이동",
    type: "move_to_published",
    icon: ArrowLeftRight,
  },
  {
    title: "숨김으로 이동",
    type: "move_to_hidden",
    icon: ArrowLeftRight,
  },
  {
    title: "삭제",
    type: "delete",
    icon: Trash2,
  },
];

// disabled 기능 추가 필요
export const PostingCard = ({
  storyId,
  selectedIds,
  thumbnail,
  title,
  description,
  groupIds,
  selected = false,
  onSelect,
  hideMoreMenu,
  isPinned,
  params,
}: PostingCardProps) => {
  const { showModal } = useModal();

  const queryClient = useQueryClient();
  const { queryKey: getStoriesQueryKey } = useGetStories();
  const { data: storyGroupListData, isSuccess: isSuccessGetStoryGroupList } =
    useGetStoryGroupList();
  const updateStoryMetadata = useUpdateStoryMetadata({
    mutation: {
      onSuccess: () => {
        return queryClient.invalidateQueries({ queryKey: getStoriesQueryKey });
      },
    },
  });
  const deleteStory = useDeleteStory();

  const handleCLick = () => {
    onSelect?.(storyId, !selected);
  };

  const handleClickMenu = async (type: any, value: any, data: any) => {
    if (type === "preview") {
      return showModal(PreviewModal, { storyId });
    }

    if (type === "edit_title") {
      return showModal(PostingUpdateModal, { storyId, name: title });
    }

    if (type === "set_as_notice") {
      const result = await updateStoryMetadata.mutateAsync({
        storyId,
        data: { pin: !isPinned },
      });
      if (!result.data.success) {
        return showModal(CommonErrorModal, {
          title: "공지 등록에 실패했습니다.",
        });
      }
    }

    if (type === "change_group") {
      if (!isSuccessGetStoryGroupList) {
        return showModal(CommonErrorModal, {
          title: "그룹 목록을 불러오는데 실패했습니다.",
        });
      }
      return showModal(PostingGroupChangeModal, {
        storyIds: [storyId],
        groupIds,
        groupListSchema:
          storyGroupListData.data.map((group) => {
            return {
              id: group.id,
              label: group.name,
              // defaultValue: true,
              type: FormFieldType.Switch,
            };
          }) ?? [],
      });
    }

    if (type === "share") {
      return showModal(SharePostingModal, { storyId });
    }

    if (type === "move_to_draft") {
      const result = await updateStoryMetadata.mutateAsync({
        storyId,
        data: { draft: true },
      });
      if (!result.data.success) {
        return showModal(CommonErrorModal, {
          title: "포스팅 이동이 실패했습니다.",
        });
      }
    }

    if (type === "move_to_published") {
      const result = await updateStoryMetadata.mutateAsync({
        storyId,
        data: { publish: true },
      });
      if (!result.data.success) {
        return showModal(CommonErrorModal, {
          title: "포스팅 이동이 실패했습니다.",
        });
      }
    }

    if (type === "move_to_hidden") {
      const result = await updateStoryMetadata.mutateAsync({
        storyId,
        data: { hide: true },
      });
      if (!result.data.success) {
        return showModal(CommonErrorModal, {
          title: "포스팅 이동이 실패했습니다.",
        });
      }
    }

    if (type === "delete") {
      const result = await deleteStory.mutateAsync({ storyId });
      if (!result.data.success) {
        return showModal(CommonErrorModal, {
          title: "포스팅 삭제에 실패했습니다.",
        });
      }
    }
  };

  // 타입 선언 필요
  const getFilteredMoreMenuItems = ({isPinned= false}: {isPinned?: boolean}): any[] => {
    // 다른 방법으로 정리할 수 있는 지 확인 필요
    if (params.draft)
      return moreMenuItems.filter(
          (menu) => menu.type !== "move_to_draft",
      );
    else if (params.hidden)
      return moreMenuItems.filter(
          (menu) => menu.type !== "move_to_hidden",
      );
    else
      return moreMenuItems.filter(
          (menu) => menu.type !== "move_to_published",
      );
  }


  return (
    <Card
      key={storyId}
      className={cn(
        "relative overflow-hidden",
        selected && "border-primary border-4",
        "cursor-pointer",
      )}
      onClick={handleCLick}
    >
      <CardContent className="p-0 h-full">
        <div className="absolute w-full h-8" style={{background: "linear-gradient(180deg, rgba(0, 0, 0, 0.40) 0%, rgba(0, 0, 0, 0.00) 100%)"}}>
          { isPinned && <img alt="pin" src="/images/pin.svg" className="absolute p-2 w-8 h-8"/> }
          { selected && <img alt="checkbox" src="/images/checkbox.svg" className="absolute p-2 w-8 h-8 right-0" style={{filter: "brightness(0) saturate(100%) invert(22%) sepia(72%) saturate(3539%) hue-rotate(255deg) brightness(95%) contrast(94%)"}}/> }
        </div>
        <div className="absolute bottom-0 w-full h-8" style={{background: "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.40) 100%)"}}/>

        <img className="h-full" alt="thumbnail" src={thumbnail} />
        {!hideMoreMenu && (
          <MoreMenu
            items={getFilteredMoreMenuItems({ isPinned })}
            onClickMenu={handleClickMenu}
            side="bottom"
            align="end"
            sideOffset={5}
            alignOffset={5}
          />
        )}
      </CardContent>
    </Card>
  );
};
